//#region Util Imports

import * as _ from 'underscore'

//#endregion

//#region Angular Imports

import { StateService } from '@uirouter/angularjs'

//#endregion

//#region DevExterme Imports

import DevExpress from 'devextreme'
import ArrayStore from 'devextreme/data/array_store'
import DataSource from 'devextreme/data/data_source'
import dxTreeList from 'devextreme/ui/tree_list'

//#endregion

import { LoggerService } from '../common.module'
import { Api, LocalDictionaryFactory } from '../../api/api.module'
import { ButtonOptions, ButtonsOptions, AdditionalFilter, GridTypes as TreeListTypes } from '../grid/grid.controller'
import { ColumnModel } from '../../api/api.models'
import { CommonHelper } from '../../../helpers/common-helper'
import { EventsNames } from '../../../helpers/events-names'
import { i18n } from '../i18n/common.i18n'
import { OperatorLayoutSettings } from '../../../helpers/operator-layout-settings'
import { TableController, TableScope } from '../common.table.controller'
import { KnownColor } from '../../../models/common/known-colors'
import { IUserSession, TemplateHelper, ConfigHelper, ReferenceTypeComponent, TypeHelper } from '../../../helpers'
import { DateBoxControlsService } from '../../../services/controls'
import { DataTypeFormatService, DataTypeFormatter } from '../../../services/data-type'
import { OperatorLayoutSetting } from '../../../models/local'
import { Enums } from '../../../models/common'
import { IMRActionFiltersOptions } from '../action-filters/action-filters.interfaces'
import { GridService } from '../../../services/grid'
import { OpView } from 'app/models/core'

export interface TreeListOptions {
	referenceId?: any
	type: number
	name: string
	localStorageVarForSavedEntity?: string
	dictionariesForSave?: any[]

	idReferenceType?: number
	dataSource?: any
	editing?: any
	selection?: any
	columnFixing?: any
	height?: any
	addCustomFilters?: boolean
	showShortDetails?: boolean
	allowColumnReordering?: boolean
	onUpdateGridsterPosition?: (e) => void
	columnAutoWidth?: boolean
	buttonsOptions: ButtonsOptions
	searchVisibleColumnsOnly?: boolean
	keyExpr?: string
	parentIdExpr?: string

	// columns
	defaultColumnsDictionary?: ColumnModel[]
	columnsDictionary?: ColumnModel[]
	columns?: DevExpress.ui.dxDataGridColumn[]

	// events
	onContentReady?(e): void
	onRowClick?(e): void
	onRowPrepared?: any
	onInitialized?(e: any, grid: TreeListController<any>): void
	onContextMenuPreparing?(e): void
	onSelectionChanged?(e): void
	onRowUpdated?(e): void
	onRowUpdating?(e): void
	onRowInserted?(e): void
	onEditorPreparing?(e): void
	onEditingStart?(e): void
	onCellPrepared?(options): void
	onNodesInitialized?(e): void
	// events: custom
	onRowDoubleClick?(e): void
	onDisposed?(): void

	contextMenuItems?: (boolean) => any

	additionalFilters?: AdditionalFilter[]
	showDatatypeLegend?: boolean

	actionFiltersOptions?: IMRActionFiltersOptions

	/**
	 * Whether to skip calling toJSON on the source object and attach it as a reference.
	 */
	skipDataSourceSerialization?: boolean
}

interface TreeListScope extends TableScope<any> {
	options: TreeListOptions

	// devexpress options
	actionFiltersPopoverOptions: DevExpress.ui.dxPopoverOptions

	// devexpress options: custom filter
	actionFiltersPopupOptions: DevExpress.ui.dxPopupOptions

	isActionFiltersPopoverVisible: boolean
	isActionFiltersPopupVisible: boolean

	needActionFiltersPopover: boolean

	isLegendShown: boolean
	isTreeListLoaded: boolean
	legendData: any
}

export class TreeListController<TSource> extends TableController<DevExpress.ui.dxTreeList, DevExpress.ui.dxTreeListOptions> {
	public static get ACTION_FILTERS_HTML_TARGET_ID(): string {
		return 'actionFilters'
	}

	//#region Consts

	private get GRID_ID_FIELD_NAME(): string {
		return 'id'
	}
	private get GRID_PARENT_OBJECT_ID_FIELD_NAME(): string {
		return '__PARENT_ID__'
	}

	//#endregion

	actionFiltersPopover: DevExpress.ui.dxPopover
	actionFiltersPopoverDeferred: ng.IDeferred<DevExpress.ui.dxPopover>

	actionFiltersPopup: DevExpress.ui.dxPopup

	private _prependedItems: TSource[] = []
	/**
	 * Used when there is no reference type option specified. Its value is set from the first object in the data source.
	 */
	private sourceObjectConstructor: new (...args: any[]) => any

	//#region Properties

	public get dxTree(): DevExpress.ui.dxTreeList {
		return this.table
	}

	public get items(): TSource[] {
		return _.map(this.dxTree.getDataSource().items(), (gridItem: any) => {
			return this.getSourceObject(gridItem)
		})
	}

	public get prependedItems(): TSource[] {
		return this._prependedItems
	}

	public get pageItemsIds(): number[] {
		return this.dxTree.getVisibleRows().map((row: DevExpress.ui.dxDataGridRowObject) => this.getSourceObjectId(row.data))
	}

	public get pageItems(): TSource[] {
		return this.dxTree.getVisibleRows().map((row: DevExpress.ui.dxDataGridRowObject) => this.getSourceObject(row.data))
	}

	public get selectedCount(): number {
		return this.dxTree.getSelectedRowKeys().length
	}

	public get selectedItemId(): number {
		return this.getSourceObjectId(this.dxTree.getSelectedRowsData()[0])
	}

	public get selectedItemsIds(): number[] {
		const selectedItems: any[] | JQueryPromise<any[]> = this.dxTree.getSelectedRowsData()

		if (!TypeHelper.isPromise(selectedItems)) {
			return selectedItems.map((gridObject: any) => this.getSourceObjectId(gridObject))
		}

		return []
	}

	public get selectedItemsIdsAsync(): JQueryPromise<number[]> {
		const selectedItems: any[] | JQueryPromise<any[]> = this.dxTree.getSelectedRowsData()

		if (TypeHelper.isPromise(selectedItems)) {
			return selectedItems.then((items: any[]) => {
				return items.map((gridObject: any) => this.getSourceObjectId(gridObject))
			})
		}

		return $.Deferred().resolve([]).promise()
	}

	public get selectedItem(): TSource {
		return this.getSourceObject(this.dxTree.getSelectedRowsData()[0])
	}

	public get selectedItems(): TSource[] {
		return this.dxTree.getSelectedRowsData().map((gridObject: any) => this.getSourceObject(gridObject))
	}

	public get selectedGridItems(): any[] {
		return this.dxTree.getSelectedRowsData()
	}

	//#endregion

	constructor(
		protected $scope: TreeListScope,
		protected $timeout: ng.ITimeoutService,
		protected $state: StateService,
		protected logger: LoggerService,
		protected $q: ng.IQService,
		protected localDictionary: LocalDictionaryFactory,
		protected api: Api,
		protected $compile: ng.ICompileService,
		protected userSession: IUserSession,
		protected dataTypeFormatService: DataTypeFormatService,
		protected dateBoxControlsService: DateBoxControlsService
	) {
		super($scope.options, $scope, $timeout, $state, logger, $q, localDictionary, api, $compile, userSession, dataTypeFormatService, dateBoxControlsService)

		$scope.$on('$destroy', () => {
			if ($scope.options.onDisposed != null) {
				$scope.options.onDisposed()
			}
		})

		dxTreeList.defaultOptions({
			options: {
				noDataText: '',
			},
		})

		this.isTreeList = true

		$scope.isTreeListLoaded = false
		if ($scope.options.showDatatypeLegend) {
			$scope.isLegendShown = OperatorLayoutSettings.getSetting('IS_SHOWN_GRID_DATATYPE_LEGEND_' + $scope.options.name, false)

			// TODO: trzeba zrobić dynamiczna legendę, ŁP: nie wie jeszcze jak
			$scope.legendData = {
				rects: [
					{ color: '##ffc0cb', description: 'Sent, not confirmed' },
					{ color: '#f0e68c', description: 'Not sent' },
				],
			}
		}

		if (OperatorLayoutSettings.getSetting('LAST_SET_COLUMN_SETTINGS_' + $scope.options.name, null) === null) {
			OperatorLayoutSettings.setSetting('LAST_SET_COLUMN_SETTINGS_' + $scope.options.name, 'default')
		}

		this.actionFiltersPopoverDeferred = $q.defer<DevExpress.ui.dxPopover>()

		this.filteredColumnName = OperatorLayoutSettings.getSetting(CommonHelper.FILTERED_COLUMN + '_' + $scope.options.name, null)
		$scope.filterText = OperatorLayoutSettings.getSetting(CommonHelper.FILTER_TEXT + '_' + $scope.options.name, null)
		if (OperatorLayoutSettings.getSetting(CommonHelper.FILTER_LABEL + '_' + $scope.options.name, null)) {
			this.$scope.filterTextLabel = OperatorLayoutSettings.getSetting(CommonHelper.FILTER_LABEL + '_' + $scope.options.name, null)
		}
		this.lastSelectedFilterColumnIndex = OperatorLayoutSettings.getSetting(CommonHelper.LAST_SELECTED_ROW + '_' + $scope.options.name, null)

		$.when<any>(this.getActiveView(), this.getDataTypeFormatter()).then((view: OpView, dataTypeFormatter: DataTypeFormatter) => {
			this.dataTypeFormatter = dataTypeFormatter

			if (view != null) {
				this.view = view
				this.primaryColumn = view.getPrimaryKeyColumn()
			}

			$scope.tableOptions = this.getTreeListOptions()

			// header buttons
			this.buttons = this.getButtons()
			$scope.headerButtons = {}
			this.buttons.forEach((button) => {
				$scope.headerButtons[button.name + $scope.tableId] = button.options
			})
			// ---

			this.setCustomFilterButtonsState()

			$scope.filterBoxOptions = <any>{
				readOnly: false,
				multiline: false,
				displayExpr: 'filter',
				onInitialized: (e) => {
					this.filterBox = e.component
				},
				onValueChanged: (e) => {
					this.tagBoxSelectionChanged(e.value, e.previousValue, e)
				},
				showClearButton: false,
				placeholder: i18n('Grid', 'NoActiveFilters'),
				disabled: false,
				opened: false,
				showSelectionControls: false,
			}

			$scope.actionFiltersPopoverOptions = {
				target: `#${TreeListController.ACTION_FILTERS_HTML_TARGET_ID}` + $scope.tableId,
				bindingOptions: {
					visible: 'isActionFiltersPopoverVisible',
				},
				onShowing: (e) => {},
				onInitialized: (e) => {
					this.actionFiltersPopover = e.component
					this.actionFiltersPopoverDeferred.resolve(e.component)
				},
				onHidden: (e) => {
					this.$scope.isActionFiltersPopoverVisible = false
				},
			}

			$scope.actionFiltersPopupOptions = {
				maxWidth: 300,
				height: 'auto',
				title: i18n('Common', 'Filters'),
				bindingOptions: {
					visible: 'isActionFiltersPopupVisible',
				},
				onHidden: () => {
					this.$scope.isActionFiltersPopupVisible = false
				},
				onInitialized: (e) => {
					this.actionFiltersPopup = e.component
				},
			}

			// hack for filter list
			$scope.$watch(
				() => {
					this.updateTotalCounterValue()
					if (this.tableElement) return this.tableElement.find('.dx-context-menu .dx-filter-menu').length
					return null
				},
				(menu) => {
					if (menu) {
						var filterMenu = this.tableElement.find('.dx-context-menu .dx-filter-menu .dx-submenu')
						var gridHeight = this.tableElement.find('.dx-datagrid-rowsview').height()
						filterMenu.dxScrollView()
						filterMenu.css({
							'max-height': gridHeight + 'px',
							overflow: 'hidden',
						})

						filterMenu.find('.dx-scrollable-container').css('max-height', gridHeight + 'px')
					}
				}
			)

			$scope.$on(EventsNames.REFRESH_ACTION_FILTERS_POPOVER_POSITION, (event: ng.IAngularEvent, ...args: any[]) => {
				this.actionFiltersPopover.option('target', '')
				this.actionFiltersPopover.option('target', `#${TreeListController.ACTION_FILTERS_HTML_TARGET_ID}` + $scope.tableId)
			})

			super.callBaseClassConstructor()

			$scope.tableOptions.keyExpr = $scope.options.keyExpr
			$scope.tableOptions.parentIdExpr = $scope.options.parentIdExpr
		})
	}

	//#region Setup

	private setupEvents(e: any): void {
		// e.component.on({
		//     'editorPreparing': (e: any) => this.onEditorPreparing(e)
		// })
	}

	//#endregion

	//#region Events

	private onEditorPreparing(e: any): void {
		if (this.$scope.options.onEditorPreparing) {
			this.$scope.options.onEditorPreparing(e)
		}

		this.setRestoreEditorFocus(e)
	}

	//#endregion

	private getButtons() {
		var buttons: ButtonOptions[] = []

		if (this.$scope.options.actionFiltersOptions != null) {
			buttons.push({
				name: TreeListController.ACTION_FILTERS_HTML_TARGET_ID, //used as id in table.controller,
				options: {
					onInitialized: (e) => {
						e.element.addClass('action-filters-button')
					},
					onClick: (e) => {
						this.$scope.needActionFiltersPopover = true
						if (this.$scope.isLargeGridHeader) {
							this.$scope.isActionFiltersPopoverVisible = !this.$scope.isActionFiltersPopoverVisible
							if (this.actionFiltersPopover) this.actionFiltersPopover.toggle(this.$scope.isActionFiltersPopoverVisible)
						} else {
							this.$scope.isActionFiltersPopupVisible = true
							if (this.actionFiltersPopup) this.actionFiltersPopup.toggle(true)
						}
					},
					template: TemplateHelper.getIcon(ConfigHelper.Icon.FILTERS, this.userSession),
					hint: i18n('Common', 'Filters'),
				},
			})
		} else {
		}

		if (this.$scope.options.buttonsOptions.onShowPreview) {
			buttons.push({
				name: 'showPreviewButton',
				options: {
					onClick: (e) => {
						if (this.$scope.options.buttonsOptions.onShowPreview()) {
							this.showPosisionPopover('#showPreviewButton' + this.$scope.tableId)
						} else {
							if (this.mapHeaderButton) this.mapHeaderButton.option('hint', i18n('Grid', 'ShowMap'))
							if (this.previewHeaderButton) this.previewHeaderButton.option('hint', i18n('Grid', 'SetPreviewPosition'))
							this.$scope.needPositionPopover = false
						}
					},
					template: TemplateHelper.getIcon(ConfigHelper.Icon.DETAILS_PREVIEW, this.userSession),
					onInitialized: (e) => {
						this.previewHeaderButtonDeferred = this.$q.defer<DevExpress.ui.dxButton>()
						this.previewHeaderButton = e.component
						this.previewHeaderButtonDeferred.resolve(e.component)
					},
					hint: i18n('Grid', 'ShowPreview'),
				},
			})
		}

		if (this.$scope.options.buttonsOptions.onShowMap) {
			buttons.push({
				name: 'showMapButton',
				options: {
					onClick: (e) => {
						if (this.$scope.options.buttonsOptions.onShowMap()) {
							this.showPosisionPopover('#showMapButton' + this.$scope.tableId)
						} else {
							if (this.previewHeaderButton) this.previewHeaderButton.option('hint', i18n('Grid', 'ShowPreview'))
							if (this.mapHeaderButton) this.mapHeaderButton.option('hint', i18n('Grid', 'SetMapPosition'))
						}
					},
					template: TemplateHelper.getIcon(ConfigHelper.Icon.MAP, this.userSession),
					onInitialized: (e) => {
						this.mapHeaderButtonDeferred = this.$q.defer<DevExpress.ui.dxButton>()
						this.mapHeaderButton = e.component
						this.mapHeaderButtonDeferred.resolve(e.component)
					},
					hint: i18n('Grid', 'ShowMap'),
				},
			})
		}

		if (this.$scope.options.buttonsOptions.showDetailsPermissions) {
			buttons.push({
				name: 'showDetailsButton',
				options: {
					onClick: (e) => {
						if (this.$scope.options.buttonsOptions.onShowClick) this.$scope.options.buttonsOptions.onShowClick(e)

						if (this.buttonsListPopover != null) {
							this.buttonsListPopover.hide()
						}
					},
					template: TemplateHelper.getIcon(ConfigHelper.Icon.DETAILS, this.userSession),
					hint: i18n('Grid', 'ShowDetails'),
					bindingOptions: {
						disabled: 'nothingSelected',
					},
				},
				permissions: this.$scope.options.buttonsOptions.showDetailsPermissions,
			})
		}

		if (this.$scope.options.buttonsOptions.addPermissions) {
			buttons.push({
				name: 'addButton',
				options: {
					onClick: (e) => {
						if (this.$scope.options.buttonsOptions.onAddClick) {
							this.$scope.options.buttonsOptions.onAddClick(null)
						}
					},
					onInitialized: (e) => {
						this.addButton = e.component
					},
					template: TemplateHelper.getIcon(ConfigHelper.Icon.ADD, this.userSession),
					hint: i18n('Grid', 'Add'),
				},
				permissions: this.$scope.options.buttonsOptions.addPermissions,
			})
		}

		if (this.$scope.options.buttonsOptions.editPermissions) {
			buttons.push({
				name: 'editButton',
				options: {
					onClick: (e) => {
						if (this.$scope.options.buttonsOptions.onEditClick) this.$scope.options.buttonsOptions.onEditClick(e)
					},
					template: TemplateHelper.getIcon(ConfigHelper.Icon.EDIT, this.userSession),
					hint: i18n('Grid', 'Edit'),
					bindingOptions: {
						disabled: 'nothingSelected',
					},
				},
				permissions: this.$scope.options.buttonsOptions.editPermissions,
			})
		}

		if (_.isFunction(this.$scope.options.buttonsOptions.onRefreshClick)) {
			buttons.push({
				name: 'refreshButton',
				options: {
					onClick: (e) => {
						this.$scope.options.buttonsOptions.onRefreshClick()
					},
					template: TemplateHelper.getIcon(ConfigHelper.Icon.REFRESH, this.userSession),
					hint: i18n('Grid', 'Refresh'),
				},
			})
		}

		if (this.$scope.options.buttonsOptions.deletePermissions) {
			buttons.push({
				name: 'deleteButton',
				options: {
					onClick: (e) => {
						if (this.$scope.options.buttonsOptions.onDeleteClick) this.$scope.options.buttonsOptions.onDeleteClick(e)
					},
					template: TemplateHelper.getIcon(ConfigHelper.Icon.DELETE, this.userSession),
					hint: i18n('Grid', 'Delete'),
					bindingOptions: {
						disabled: 'nothingSelected',
					},
				},
				permissions: this.$scope.options.buttonsOptions.deletePermissions,
			})
		}

		buttons.push({
			name: 'columnFixingButton',
			options: {
				onClick: () => {
					this.changeColumnFixing()
					this.colorFixingButton()
					$('.dx-button').blur()
				},
				template: TemplateHelper.getIcon(ConfigHelper.Icon.FORMAT_INCREASE, this.userSession),
				hint: i18n('Grid', 'ColumnFixing'),
			},
		})

		buttons.push({
			name: 'columnChooserButton',
			options: {
				onClick: () => {
					this.table.showColumnChooser()
					this.$timeout(() => {
						this.setColumnChooserTooltip()
					}, 500)
				},
				hint: i18n('Grid', 'ColumnChooser'),
				template: TemplateHelper.getIcon(ConfigHelper.Icon.COLUMN_CHOOSER, this.userSession),
			},
		})

		if (this.$scope.options.buttonsOptions.saveColumnSettingsPermissions) {
			buttons.push({
				name: 'saveColumnSettingsButton',
				options: {
					onClick: () => {
						this.$scope.needSaveColumnSettings = true
						this.$timeout(() => {
							this.$scope.$broadcast(EventsNames.SHOW_SAVE_COLUMN_SETTINGS + this.$scope.tableId, this.$scope.isLargeGridHeader)
						})
					},
					hint: i18n('Grid', 'ColumnConfiguration'),
					template: TemplateHelper.getIcon(ConfigHelper.Icon.COLUMN_SETTINGS, this.userSession),
				},
				permissions: this.$scope.options.buttonsOptions.saveColumnSettingsPermissions,
			})
		}

		buttons.push({
			name: 'filterButton',
			options: {
				onClick: () => {
					this.$scope.needCustomFilter = true
					this.$timeout(() => {
						if (this.$scope.isLargeGridHeader) {
							this.$scope.isCustomFilterPopoverVisible = !this.$scope.isCustomFilterPopoverVisible
							this.filterPopover.toggle(this.$scope.isCustomFilterPopoverVisible)
							if (!this.$scope.isCustomFilterPopoverVisible) $('.dx-button').blur()
						} else {
							this.$scope.isFilterListPopupVisible = true
						}
					})
				},
				hint: i18n('Grid', 'CustomFiltering'),
				template: TemplateHelper.getIcon(ConfigHelper.Icon.FILTER, this.userSession),
			},
		})

		if (this.$scope.options.showDatatypeLegend) {
			buttons.push({
				name: 'datatypeLegendButton',
				options: {
					onClick: () => {
						this.$scope.isLegendShown = !this.$scope.isLegendShown
						OperatorLayoutSettings.setSetting('IS_SHOWN_GRID_DATATYPE_LEGEND_' + this.$scope.options.name, this.$scope.isLegendShown)
					},
					hint: i18n('Common', 'Legend'),
					template: TemplateHelper.getIcon(ConfigHelper.Icon.LEGEND, this.userSession),
				},
			})
		}

		return buttons
	}

	private getTreeListOptions(): any {
		var onInitializeData: (e) => void

		if (this.$scope.options.dataSource) {
			this.dataSource = this.$scope.options.dataSource
			if (this.dataSource.store && angular.isFunction(this.dataSource.store)) {
				this.dataSource
					.store()
					.totalCount()
					.then((totalCount) => {
						this.$scope.totalCount = totalCount
					})
			} else if (this.dataSource['_array']) {
				this.$scope.totalCount = this.dataSource['_array'].filter((item) => {
					if (item.id || item.id == 0) return item
				}).length
			}
			this.isFilteredLocally = true
		} else {
			var dataSourceOptions = {
				store: new ArrayStore({ data: [] }),
			}
			var dataSource = new DataSource(dataSourceOptions)
			dataSource.pageSize(100)
			this.dataSource = dataSource
			this.$scope.totalCount = 0
		}

		// Columns
		var columns: DevExpress.ui.dxDataGridColumn[] = []

		if (this.$scope.options.columns) {
			this.referenceTables = {}
			this.$scope.options.columns.map((col) =>
				TableController.addCustomFiltersToDxColumn(col, col.dataType, col, this.$scope.options.addCustomFilters, this.localDictionary, this.referenceTables, this)
			)
			columns = this.$scope.options.columns
		} else if (this.$scope.options.defaultColumnsDictionary && this.$scope.options.columnsDictionary) {
			this.$scope.options.defaultColumnsDictionary.forEach((defaultColumn) => {
				var exist = false
				this.$scope.options.columnsDictionary.forEach((column) => {
					if (defaultColumn.id == column.id) exist = true
				})
				if (!exist) this.$scope.options.columnsDictionary.push(defaultColumn)
			})
			columns = this.getDxColumns(this.$scope.options.columnsDictionary, this.$scope.options.defaultColumnsDictionary)
		} else if (this.$scope.options.columnsDictionary) {
			columns = this.getDxColumns(this.$scope.options.columnsDictionary)
		} else {
			alert('Problem with columns. Please contact with admin.')
		}

		var onContentReady = _.debounce((e) => {
			this.onContentReady(e)
		}, 200)

		var $window = angular.element(window)
		this.windowHeight = $window.height()
		this.windowWidth = $window.width()

		if (this.windowHeight > this.windowWidth && this.windowWidth <= 768) {
			this.isMobile = true
		} else if (this.windowHeight < this.windowWidth && this.windowWidth <= 1024) {
			this.isMobile = true
		} else {
			this.isMobile = false
		}

		var treeListOptions: any = {
			onInitialized: (e) => {
				this.table = e.component
				this.tableElement = e.element
				this.tableDeferred.resolve(e.component)

				if (this.$scope.options.onInitialized) {
					e.tableId = this.$scope.tableId
					this.$scope.options.onInitialized(e, this)
				}

				this.$scope.show = true
				this.hackForCustomFilters(e)
				this.setupEvents(e)

				if (onInitializeData) onInitializeData(e)
			},
			remoteOperations: {
				filtering: true,
				sorting: true,
			},
			autoExpandAll: true,
			onContentReady: onContentReady,
			columns: columns,
			dataSource: this.dataSource,
			height: '100%',
			columnFixing: {
				enabled: this.$scope.options.columnAutoWidth,
				texts: {
					fix: i18n('Grid', 'Fix'),
					leftPosition: i18n('Grid', 'LeftPosition'),
					rightPosition: i18n('Grid', 'RightPosition'),
					unfix: i18n('Grid', 'Unfix'),
				},
			},
			noDataText: i18n('Common', 'NoData'),
			columnChooser: <any>{
				enabled: true,
				emptyPanelText: i18n('Grid', 'ColumnChooserText'),
				title: i18n('Grid', 'ColumnChooserTitle'),
				mode: 'select',
			},
			hoverStateEnabled: true,
			rowAlternationEnabled: true,
			allowColumnReordering: true,
			allowColumnResizing: true,
			sorting: {
				mode: 'multiple',
				ascendingText: i18n('Grid', 'SortAscending'),
				clearText: i18n('Grid', 'ClearSorting'),
				descendingText: i18n('Grid', 'SortDescending'),
			},
			loadPanel: {
				text: i18n('Grid', 'Loading'),
				enabled: false,
			},
			filterRow: {
				visible: true,
				showOperationChooser: true,
				operationDescriptions: {
					equal: i18n('Common', 'Equal'),
					notEqual: i18n('Common', 'NotEqual'),
					lessThan: i18n('Common', 'LessThan'),
					lessThanOrEqual: i18n('Common', 'LessThanOrEqual'),
					greaterThan: i18n('Common', 'GreaterThan'),
					greaterThanOrEqual: i18n('Common', 'GreaterThanOrEqual'),
					startsWith: i18n('Common', 'StartsWith'),
					contains: i18n('Common', 'Contains'),
					notContains: i18n('Common', 'NotContains'),
					endsWith: i18n('Common', 'EndsWith'),
					between: i18n('Common', 'Between'),
				},
				resetOperationText: i18n('Common', 'Reset'),
				showAllText: i18n('Common', 'All'),
			},
			searchPanel: <any>{
				visible: this.$scope.options.buttonsOptions.enabled,
				placeholder: i18n('Common', 'Search'),
				searchVisibleColumnsOnly: this.$scope.options.searchVisibleColumnsOnly != null ? this.$scope.options.searchVisibleColumnsOnly : true,
			},
			scrolling: {
				mode: 'virtual',
				preloadEnabled: true,
			},
			columnAutoWidth: this.isMobile ? true : this.$scope.options.columnAutoWidth,
			selection: {
				mode: 'single',
			},
			stateStoring: {
				enabled: this.$scope.options.name != null,
				type: 'custom',
				storageKey: this.$scope.options.name,
				customLoad: () => {
					const tableConfig: any = this.userSession.getOperatorLayoutSetting(new OperatorLayoutSetting(Enums.SimaxOperatorLayoutSetting.GridOptions, this.$scope.options.name), null)

					// Setting custom properties
					if (tableConfig) {
						this.table.option('columnAutoWidth', tableConfig.columnAutoWidth)
					}

					return tableConfig
				},
				customSave: (gridState: any) => {
					// Custom saving columnAutoWidth
					gridState.columnAutoWidth = this.table.option('columnAutoWidth')
					this.userSession.setOperatorLayoutSetting(new OperatorLayoutSetting(Enums.SimaxOperatorLayoutSetting.GridOptions, this.$scope.options.name), gridState)
				},
				savingTimeout: 0,
			},
			onRowClick: (e) => {
				if (this.$scope.multiSelectEnabled) {
					if (this.selectedRows.filter((row) => row == e.rowIndex).length) {
						var index = this.selectedRows.indexOf(e.rowIndex)
						if (index > -1) {
							this.selectedRows.splice(index, 1)
						}
					} else {
						this.selectedRows.push(e.rowIndex)
					}
					e.component.selectRowsByIndexes(this.selectedRows)
				} else {
					this.onRowClick(e)
				}
			},
			onContextMenuPreparing: (e: any) => {
				var data = e.component.getSelectedRowsData()
				if (data.length == 0) return
				else if (this.$scope.options.onContextMenuPreparing) {
					this.$scope.options.onContextMenuPreparing(e)
				}
			},
			onSelectionChanged: (e) => {
				this.onSelectionChanged(e)
			},
			onToolbarPreparing: (e) => {
				let toolbarItems = e.toolbarOptions.items

				toolbarItems.forEach(function (item) {
					if (item.name === 'columnChooserButton') {
						item.options.visible = false
					}
				})
			},
		}

		if (this.$scope.options.onRowUpdated) {
			treeListOptions.onRowUpdated = (e) => {
				this.$scope.options.onRowUpdated(e)
			}
		}

		if (this.$scope.options.onRowUpdating) {
			treeListOptions.onRowUpdating = (e) => {
				this.$scope.options.onRowUpdating(e)
			}
		}

		if (this.$scope.options.onRowInserted) {
			treeListOptions.onRowInserted = (e) => {
				this.$scope.options.onRowInserted(e)
			}
		}

		if (this.$scope.options.onCellPrepared) {
			treeListOptions.onCellPrepared = (e) => {
				this.$scope.options.onCellPrepared(e)
			}
		}

		if (this.$scope.options.onEditingStart) {
			treeListOptions.onEditingStart = (e) => {
				this.$scope.options.onEditingStart(e)
			}
		}

		if (this.$scope.options.onEditorPreparing) {
			treeListOptions.onEditorPreparing = (e) => {
				this.$scope.options.onEditorPreparing(e)
			}
		}

		if (this.$scope.options.onNodesInitialized) {
			treeListOptions.onNodesInitialized = (e) => {
				this.$scope.options.onNodesInitialized(e)
			}
		}

		if (this.$scope.options.onRowPrepared) {
			if (<any>this.$scope.options.onRowPrepared == 'edit') {
				treeListOptions.onRowPrepared = (e: any) => {
					if (e.rowType == 'data') {
						if (e.data.isNew) {
							$(e.rowElement).css('font-style', 'italic')
						} else if (e.data.isRemoved) {
							$(e.rowElement).css('text-decoration', 'line-through')
						} else if (e.data.isEdited) {
							$(e.rowElement).css('font-weight', 'bold')
						}
					}
				}
			} else {
				treeListOptions.onRowPrepared = (e) => {
					this.$scope.options.onRowPrepared(e)
				}
			}
		}

		treeListOptions.loadPanel.enabled = true
		if (this.$scope.options.contextMenuItems) {
			treeListOptions.onContextMenuPreparing = (e: any) => {
				if (e.row && e.row.rowType === 'data') {
					if (!this.table.isRowSelected(e.row.key)) {
						this.table.selectRows(e.row.key, false)
					}

					this.$scope.options.contextMenuItems(this.isTouchScreen).then((items) => {
						if (items.length == 0) {
							return
						}
						e.items = items
					})
				}
			}
		}

		var onRowPrepared = treeListOptions.onRowPrepared

		treeListOptions.onRowPrepared = (e: any) => {
			if (onRowPrepared) onRowPrepared(e)
			e.rowElement.on('swiperight', () => {
				this.selectedRows.push(e.rowIndex)
				e.component.selectRowsByIndexes(this.selectedRows)
			})
			e.rowElement.on('swipeleft', () => {
				e.component.deselectRows([e.key])
				var index = this.selectedRows.indexOf(e.rowIndex)
				if (index > -1) {
					this.selectedRows.splice(index, 1)
				}
			})
			if (['header', 'filter', 'totalFooter'].indexOf(e.rowType) < 0 && e.data && e.data.rowColor) {
				if (e.data.rowColor >= 0) {
					e.rowElement.find('td').css('background-color', 'rgba(' + CommonHelper.rbgaToRgbColor(KnownColor.getColor(e.data.rowColor.toString())) + ', 0.65)')
				} else {
					e.rowElement.find('td').css('background-color', 'rgba(' + CommonHelper.argbToRGB(e.data.rowColor.toString()) + ', 0.65)')
				}
			}
		}

		switch (this.$scope.options.type) {
			case TreeListTypes.MAIN:
				break
		}

		if (this.$scope.options.height) {
			treeListOptions.height = this.$scope.options.height
		}

		if (this.$scope.options.editing) treeListOptions.editing = this.$scope.options.editing
		if (this.$scope.options.columnFixing) treeListOptions.columnFixing = this.$scope.options.columnFixing
		if (this.$scope.options.allowColumnReordering != null) treeListOptions.allowColumnReordering = this.$scope.options.allowColumnReordering
		return treeListOptions
	}

	private onContentReady(e) {
		this.table = e.component
		this.tableElement = e.element

		if (e.element.find('.custom-grid-button').length == 0 && this.$scope.options.buttonsOptions.enabled) {
			this.addButtons('#treeListContainer' + this.$scope.tableId)
		}
		if (this.$scope.options.onContentReady) this.$scope.options.onContentReady(e)

		if (this.$scope.options.showShortDetails) {
			var elementsToRemove = $(e.element).find('.dx-command-expand')
			elementsToRemove.closest('table').find('> colgroup > col:first-child').width(0)
			elementsToRemove.width(0)

			var visibleColumns: any[] = e.component._controllers.columns._visibleColumns[0]
			if (!angular.equals(visibleColumns, this.lastVisibleColumns)) {
				if (this.onChangeColumnsTimeout) clearTimeout(this.onChangeColumnsTimeout)
				this.onChangeColumnsTimeout = setTimeout(() => {
					this.lastVisibleColumns = visibleColumns
					e.component.collapseAll(-1)
					e.component.deselectAll()
				}, 100)
			}
		}

		this.refreshFilters()

		// hack for dx-freespace-row
		this.$timeout(() => {
			var contentTable = this.tableElement.find('.dx-treelist-rowsview .dx-treelist-content > .dx-treelist-table').first()
			var contentTableRows = contentTable.find('.dx-row')
			if (contentTableRows.length > 1) {
				var rowsHeight = 0
				for (var i = 0; i < contentTableRows.length; i++) {
					var columnRow = angular.element(contentTableRows[i])
					if (!columnRow.hasClass('dx-freespace-row')) {
						rowsHeight += columnRow.outerHeight()
					}
				}
				var parentHeight = contentTable.parent().parent().parent().parent().height()
				if (parentHeight - rowsHeight > 0) {
					contentTableRows
						.last()
						.height(parentHeight - rowsHeight - 1)
						.addClass('tree-list__freespace-hack')
				} else {
					contentTableRows.last().height(0)
				}
			}
		})

		this.$scope.isTreeListLoaded = true
	}

	addButtons(selector) {
		var header = angular.element(selector).find('.dx-treelist-header-panel .dx-toolbar > .dx-toolbar-items-container > .dx-toolbar-after')
		if (this.buttons && this.buttons.length > 0) {
			var buttonsString = '<div>'
			this.buttons.forEach((options) => {
				if (
					options.name == 'selectionButton' ||
					options.name == 'showPreviewButton' ||
					options.name == 'showDetailsButton' ||
					options.name == 'actionFilters' ||
					options.name == 'addButton' ||
					options.name == 'editButton' ||
					options.name == 'refreshButton'
				) {
					buttonsString +=
						'<div ng-if="isLargeGridHeader" class="custom-grid-button dx-toolbar-button dx-toolbar-menu-container"><div class="dx-item-content dx-toolbar-item-content"><div id="' +
						options.name +
						this.$scope.tableId +
						'" dx-button="headerButtons[\'' +
						options.name +
						this.$scope.tableId +
						'\']"></div></div></div>'
				}
			})
			buttonsString +=
				'<div class="custom-grid-button dx-toolbar-button dx-toolbar-menu-container"><div class="dx-item-content dx-toolbar-item-content"><div id="popoverListButton' +
				this.$scope.tableId +
				'" dx-button="popoverListButtonOptions"></div></div></div>'
			buttonsString += '</div>'
			header.prepend(this.$compile(buttonsString)(this.$scope))
		}
		this.$scope.$broadcast('customButtonsGridAdded', header)
	}

	private updateTotalCounterValue(): void {
		if (this.dataSource && this.dataSource['_array'] && this.dataSource['_array'].length != this.$scope.totalCount) {
			this.$scope.totalCount = this.dataSource['_array'].length
		}
	}

	/**
	 * Returns the business object attached to the given grid object.
	 */
	public getSourceObject(gridObject: any): TSource {
		if (gridObject != null) {
			const sourceObjectJSON: any = gridObject[GridService.SOURCE_OBJECT_FIELD_NAME]

			if (this.$scope.options.skipDataSourceSerialization) {
				return sourceObjectJSON
			}

			if (this.$scope.options.idReferenceType != null) {
				return ReferenceTypeComponent.createReferenceObject(this.$scope.options.idReferenceType, sourceObjectJSON) as any
			}

			if (this.sourceObjectConstructor != null) {
				return new this.sourceObjectConstructor(sourceObjectJSON)
			}
		}

		return null
	}

	/**
	 * Returns the id of the source object attached to the given grid object
	 */
	private getSourceObjectId(gridObject: any): number {
		return gridObject[this.GRID_PARENT_OBJECT_ID_FIELD_NAME] || gridObject[this.GRID_ID_FIELD_NAME]
	}
}
